import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  EnterOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined,
  FilePdfOutlined, DownloadOutlined, CheckOutlined, PhoneOutlined, FolderOpenOutlined,
} from '@ant-design/icons';
import Avatar from './Avatar';
import conversationService from '../../services/conversationService';
import { decouperNumerosSinistre, useOuvrirDossierSinistre } from './liensSinistre';
import { decouperDossiers, decouperLiens, decouperMentions, heure, T, tailleLisible } from './utils';

/**
 * Une bulle du fil.
 *
 * TEXTE, TOUJOURS EN TEXTE : le contenu est rendu en nœuds React, les liens par
 * découpage puis <a rel="noopener noreferrer">. Aucun HTML injecté.
 *
 * La priorité se lit par une petite étiquette (ambre ou rouge) en tête de bulle -
 * jamais par une barre latérale ni un fond entièrement coloré : le fil doit rester
 * calme même avec plusieurs urgents.
 */

/**
 * Les numeros de sinistre cites deviennent des puces cliquables qui ouvrent le
 * dossier. La detection ne s'applique qu'aux fragments TEXTE de `decouperLiens` :
 * une URL contient des barres obliques et des chiffres, elle serait decoupee a
 * tort. Puce bordee plutot que lien souligne - un dossier n'est pas une adresse
 * web, et les deux cohabitent dans le meme message.
 *
 * Les mentions sont découpées AVANT les liens et les numéros : un marqueur
 * contient des chiffres, des tirets et des deux-points - il serait pris pour un
 * numéro de dossier ou une URL. Ma propre mention ressort en ambre : dans un fil
 * de quarante messages, ce qui compte d'abord est de retrouver ce qui m'était adressé.
 */
/**
 * Dossier cité par `#` : la puce porte l'identifiant du dossier et l'ouvre
 * directement - aucune recherche, donc jamais la liste des sinistres à la place.
 */
const PuceDossier = ({ numero, sinistreTrackingId }) => {
  const { t } = useTranslation('conversations');
  const navigate = useNavigate();
  return (
    <button
      type="button" title={t('bulle.ouvrirDossier', { numero })}
      onClick={() => navigate(`/sinistres/${sinistreTrackingId}`)}
      style={{
        font: 'inherit', fontWeight: 600, color: T.vert, background: T.vertXpale,
        border: `1px solid ${T.bord}`, borderRadius: 6, padding: '0 6px',
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    ><FolderOpenOutlined style={{ fontSize: 12, marginRight: 4 }} />{numero}</button>
  );
};

const Contenu = ({ texte, moiTrackingId }) => {
  const { t } = useTranslation('conversations');
  const ouvrirDossier = useOuvrirDossierSinistre();
  const rendreTexte = (morceau, cle) => decouperLiens(morceau).map((f, i) => {
    if (f.type === 'lien') {
      return <a key={`${cle}-${i}`} href={f.valeur} target="_blank" rel="noopener noreferrer" style={{ color: T.vert, textDecoration: 'underline' }}>{f.valeur}</a>;
    }
    return decouperNumerosSinistre(f.valeur).map((g, j) => (g.type === 'sinistre'
      ? (
        <button
          key={`${cle}-${i}-${j}`} type="button" title={t('bulle.ouvrirDossier', { numero: g.valeur })}
          onClick={() => ouvrirDossier(g.valeur)}
          style={{
            font: 'inherit', fontWeight: 600, color: T.vert, background: T.blanc,
            border: `1px solid ${T.bord}`, borderRadius: 6, padding: '0 6px',
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >{g.valeur}</button>
      )
      : <React.Fragment key={`${cle}-${i}-${j}`}>{g.valeur}</React.Fragment>));
  });
  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {decouperMentions(texte).map((frag, k) => {
        // Les dossiers cités sont extraits AVANT les liens et les numéros bruts : le
        // marqueur contient le numéro et un UUID, il serait sinon découpé à tort.
        if (frag.type === 'texte') {
          return (
            <React.Fragment key={k}>
              {decouperDossiers(frag.valeur).map((d, n) => (d.type === 'dossier'
                ? <PuceDossier key={`${k}-d${n}`} numero={d.valeur} sinistreTrackingId={d.sinistreTrackingId} />
                : <React.Fragment key={`${k}-d${n}`}>{rendreTexte(d.valeur, `${k}-${n}`)}</React.Fragment>))}
            </React.Fragment>
          );
        }
        const cEstMoi = !!moiTrackingId && frag.utilisateurTrackingId === moiTrackingId;
        return (
          <span key={k} title={cEstMoi ? t('bulle.mentionne') : undefined}
            style={{
              background: cEstMoi ? T.ambreFond : T.blanc, color: cEstMoi ? T.ambre : T.vert,
              border: `1px solid ${cEstMoi ? '#fcd34d' : T.bord}`, borderRadius: 6, padding: '0 6px',
              fontWeight: 600, whiteSpace: 'nowrap',
            }}>
            @{frag.valeur}
          </span>
        );
      })}
    </span>
  );
};

/** Image servie par une route protégée : on la charge en blob, jamais par <img src=URL>. */
const ImageJointe = ({ piece }) => {
  const { t } = useTranslation('conversations');
  const [src, setSrc] = useState(null);
  const [erreur, setErreur] = useState(false);
  useEffect(() => {
    let url = null;
    let actif = true;
    conversationService.lirePiece(piece.pieceTrackingId).then((blob) => {
      if (!actif) return;
      url = URL.createObjectURL(blob);
      setSrc(url);
    }).catch(() => actif && setErreur(true));
    return () => { actif = false; if (url) URL.revokeObjectURL(url); };
  }, [piece.pieceTrackingId]);

  const ratio = piece.largeur && piece.hauteur ? piece.hauteur / piece.largeur : 0.66;
  const largeur = Math.min(320, piece.largeur || 320);
  if (erreur) return <span style={{ fontSize: T.taille.meta, color: T.muet }}>{t('bulle.imageIndisponible')}</span>;
  return (
    <a href={src || '#'} target="_blank" rel="noopener noreferrer" onClick={(e) => !src && e.preventDefault()}
      style={{ display: 'block', width: largeur, maxWidth: '100%' }}>
      {src ? (
        <img src={src} alt={piece.nomOriginal} style={{ width: '100%', borderRadius: T.rayon.champ, display: 'block', border: `1px solid ${T.bord}` }} />
      ) : (
        <div style={{ width: '100%', paddingTop: `${ratio * 100}%`, background: T.fondBulle, borderRadius: T.rayon.champ }} />
      )}
    </a>
  );
};

const PieceJointe = ({ piece }) => {
  const telecharger = async (e) => {
    e.preventDefault();
    const blob = await conversationService.lirePiece(piece.pieceTrackingId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = piece.nomOriginal; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  if (piece.image) return <ImageJointe piece={piece} />;
  return (
    <a href="#pj" onClick={telecharger}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', minHeight: 36,
        border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ, background: T.blanc, color: T.texte,
        textDecoration: 'none', fontSize: T.taille.meta,
      }}>
      <FilePdfOutlined style={{ fontSize: 20, color: '#b91c1c' }} />
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{piece.nomOriginal}</span>
      <span style={{ color: T.muet }}>{tailleLisible(piece.taille)}</span>
      <DownloadOutlined style={{ color: T.muet }} />
    </a>
  );
};

/**
 * Trace d'appel, rendue comme sur un téléphone : icône, « Appel vocal manqué »
 * en rouge ou « Appel vocal · 3 min », l'heure, et « Rappeler ».
 */
const lireAppel = (contenu) => { try { return JSON.parse(contenu || '{}'); } catch { return {}; } };
const dureeLisible = (s) => {
  if (!s) return '';
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
};

const BulleAppel = ({ m, surRappeler }) => {
  const { t } = useTranslation('conversations');
  const a = lireAppel(m.contenu);
  const manque = ['SANS_REPONSE', 'ANNULE', 'REFUSE', 'OCCUPE'].includes(a.issue);
  const interrompu = a.issue === 'ECHEC';
  const libelle = manque ? (m.parMoi ? t('appel.sansReponse') : t('appel.manque')) : interrompu ? t('appel.interrompu') : t('appel.vocal');
  const couleur = manque && !m.parMoi ? T.rouge : T.texte;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, minWidth: 240, padding: '10px 12px', borderRadius: T.rayon.carte,
      background: m.parMoi ? T.vertPale : T.fondBulle,
    }}>
      <span aria-hidden="true" style={{
        width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: manque && !m.parMoi ? T.rougeFond : T.blanc, color: manque && !m.parMoi ? T.rouge : T.vert, fontSize: 16,
      }}>
        <PhoneOutlined style={{ transform: m.parMoi ? 'rotate(0deg)' : 'rotate(225deg)' }} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: T.taille.corps, fontWeight: 600, color: couleur }}>{libelle}</span>
        <span style={{ display: 'block', fontSize: T.taille.meta, color: T.muet }}>
          {heure(m.creeLe)}{!manque && !interrompu && a.dureeSecondes ? ` · ${dureeLisible(a.dureeSecondes)}` : ''}
        </span>
      </span>
      {surRappeler && (
        <button type="button" onClick={surRappeler}
          style={{ height: 32, padding: '0 12px', borderRadius: 16, border: `1px solid ${T.bord}`, background: T.blanc, color: T.vert, font: 'inherit', fontSize: T.taille.meta, fontWeight: 600, cursor: 'pointer' }}>
          {t('appel.rappeler')}
        </button>
      )}
    </div>
  );
};

const Etiquette = ({ priorite }) => {
  const pastille = { display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, borderRadius: 4, padding: '0 6px', lineHeight: '18px' };
  if (priorite === 'IMPORTANT') return <span style={{ ...pastille, color: T.ambre, background: T.ambreFond }}>IMPORTANT</span>;
  if (priorite === 'URGENT') return <span style={{ ...pastille, color: '#fff', background: T.rouge }}>URGENT</span>;
  return null;
};

const Bouton = ({ titre, onClick, children, danger }) => (
  <Tooltip title={titre}>
    <button type="button" onClick={onClick} aria-label={titre}
      style={{
        width: 28, height: 28, border: 'none', background: 'transparent', borderRadius: T.rayon.champ,
        cursor: 'pointer', color: danger ? T.rouge : T.muet, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      {children}
    </button>
  </Tooltip>
);

export default function MessageBulle({
  message: m, teteDeGroupe, dernierEnvoye, lecteurs, surRepondre, surModifier, surSupprimer, surReessayer, surAllerVers, surRappeler,
  moiTrackingId,
}) {
  const { t } = useTranslation('conversations');
  const [survol, setSurvol] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [brouillon, setBrouillon] = useState(m.contenu || '');
  const zone = useRef(null);

  useEffect(() => { if (enEdition) zone.current?.focus(); }, [enEdition]);

  if (m.type === 'SYSTEME') {
    return (
      <div style={{ textAlign: 'center', fontSize: T.taille.mini, color: T.muet, padding: '4px 0' }}>{m.contenu}</div>
    );
  }
  if (m.type === 'APPEL') {
    return (
      <div style={{ display: 'flex', justifyContent: m.parMoi ? 'flex-end' : 'flex-start', marginTop: teteDeGroupe ? 16 : 4, paddingLeft: m.parMoi ? 0 : 40 }}>
        <BulleAppel m={m} surRappeler={surRappeler} />
      </div>
    );
  }

  const enAttente = m.etat === 'attente';
  const enErreur = m.etat === 'erreur';
  const couleurBarre = m.priorite === 'URGENT' ? T.rouge : m.priorite === 'IMPORTANT' ? T.ambre : null;

  const valider = () => {
    const t = brouillon.trim();
    if (t && t !== m.contenu) surModifier(m, t);
    setEnEdition(false);
  };

  return (
    <div
      onMouseEnter={() => setSurvol(true)} onMouseLeave={() => setSurvol(false)}
      style={{
        display: 'flex', flexDirection: m.parMoi ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8,
        marginTop: teteDeGroupe ? 16 : 4, opacity: enAttente || enErreur ? 0.6 : 1,
      }}
    >
      {!m.parMoi && (
        <span style={{ width: 32, flexShrink: 0 }}>
          {teteDeGroupe && <Avatar nom={m.auteurNom} initiales={m.auteurInitiales} taille={32} />}
        </span>
      )}
      <div style={{ maxWidth: 560, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: m.parMoi ? 'flex-end' : 'flex-start' }}>
        {teteDeGroupe && !m.parMoi && (
          <span style={{ fontSize: T.taille.meta, fontWeight: 600, color: T.texte2, margin: '0 0 4px 12px' }}>{m.auteurNom}</span>
        )}
        <div style={{ position: 'relative' }}>
          <div
            style={{
              background: m.parMoi ? T.vertPale : T.fondBulle, color: T.texte, borderRadius: T.rayon.carte,
              padding: '8px 12px', fontSize: T.taille.corps, lineHeight: 1.5,
            }}
          >
            {couleurBarre && !m.supprime && <div style={{ marginBottom: 4 }}><Etiquette priorite={m.priorite} /></div>}
            {m.reponseA && (
              <button type="button" onClick={() => surAllerVers?.(m.reponseA.messageTrackingId)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', marginBottom: 8,
                  background: 'rgba(0,0,0,0.05)', border: 'none',
                  borderRadius: T.rayon.champ, padding: '4px 8px', fontSize: T.taille.meta, color: T.texte2,
                }}>
                <span style={{ fontWeight: 600, color: T.vert }}>{m.reponseA.auteurNom}</span><br />
                <span style={{ fontStyle: m.reponseA.supprime ? 'italic' : 'normal', color: m.reponseA.supprime ? T.muet : T.texte2 }}>
                  {m.reponseA.supprime ? t('bulle.messageSupprime') : m.reponseA.extrait}
                </span>
              </button>
            )}
            {m.supprime ? (
              <span style={{ fontStyle: 'italic', color: T.muet }}>{t('bulle.messageSupprime')}</span>
            ) : enEdition ? (
              <div>
                <textarea ref={zone} value={brouillon} onChange={(e) => setBrouillon(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); valider(); }
                    if (e.key === 'Escape') { setEnEdition(false); setBrouillon(m.contenu); }
                  }}
                  rows={Math.min(6, Math.max(1, brouillon.split('\n').length))}
                  style={{
                    width: 420, maxWidth: '100%', font: 'inherit', fontSize: T.taille.corps, lineHeight: 1.5, padding: 8,
                    border: `1px solid ${T.vert}`, borderRadius: T.rayon.champ, resize: 'none', outline: 'none', background: T.blanc,
                  }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: T.taille.mini, color: T.muet }}>
                  <span>{t('bulle.aideEdition')}</span>
                </div>
              </div>
            ) : (
              <>
                {m.contenu && <Contenu texte={m.contenu} moiTrackingId={moiTrackingId} />}
                {m.piecesJointes?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: m.contenu ? 8 : 0 }}>
                    {m.piecesJointes.map((p) => <PieceJointe key={p.pieceTrackingId} piece={p} />)}
                  </div>
                )}
              </>
            )}
          </div>

          {survol && !m.supprime && !enAttente && !enErreur && !enEdition && (
            <div
              style={{
                position: 'absolute', top: -14, [m.parMoi ? 'left' : 'right']: 0, display: 'flex',
                background: T.blanc, border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ, padding: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <Bouton titre={t('bulle.repondre')} onClick={() => surRepondre(m)}><EnterOutlined /></Bouton>
              {m.modifiable && <Bouton titre={t('bulle.modifier')} onClick={() => { setBrouillon(m.contenu || ''); setEnEdition(true); }}><EditOutlined /></Bouton>}
              {m.modifiable && (
                <Popconfirm title={t('bulle.supprimerConfirmation')} okText={t('bulle.supprimer')} cancelText={t('bulle.annuler')} onConfirm={() => surSupprimer(m)}>
                  <span><Bouton titre={t('bulle.supprimer')} danger><DeleteOutlined /></Bouton></span>
                </Popconfirm>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: T.taille.mini, color: T.muet, marginTop: 2, padding: '0 4px' }}>
          {enErreur ? (
            <>
              <ExclamationCircleOutlined style={{ color: T.rouge }} />
              <span style={{ color: T.rouge }}>{t('bulle.nonEnvoye')}</span>
              <button type="button" onClick={() => surReessayer(m)}
                style={{ border: 'none', background: 'none', color: T.vert, cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>
                <ReloadOutlined /> {t('bulle.reessayer')}
              </button>
            </>
          ) : enAttente ? (
            <span>{t('bulle.enAttente')}</span>
          ) : (
            <>
              <span>{heure(m.creeLe)}</span>
              {m.modifieLe && !m.supprime && <span>· {t('bulle.modifie')}</span>}
              {dernierEnvoye && lecteurs?.length > 0 && (
                <Tooltip title={t('bulle.luPar', { noms: lecteurs.join(', ') })}>
                  <span style={{ color: T.vert }}><CheckOutlined /><CheckOutlined style={{ marginLeft: -6 }} /></span>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
